import {
  Component, HostBinding, OnChanges, OnInit, SimpleChanges, ViewEncapsulation
} from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { MediaResolved } from 'idt-ecommerce-api/catalog/products/medias/medias.models';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { Product, ProductPartNumberDetails } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { CMSEvent } from 'render/models/cms-event.model';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { iif, Observable, of } from 'rxjs';
import { PRODUCT_DESCRIPTION_DEFAULT_VALUE, PRODUCT_NAME_DEFAULT_VALUE } from 'shared/idt-components/idt-components-cms.model';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { PieceProductSettings, ProductImageLocations } from './models/piece-product.models';
import { styleUrls, templateUrl } from './piece-product-render.component.paths';


export interface ImageDetails extends ProductPartNumberDetails {
  location?: ProductImageLocations;
  viewStyle?: string;
  viewStyleMediaId?: string;
}


@Component({
  selector: 'piece-product-render',
  templateUrl,
  styleUrls,
  encapsulation: ViewEncapsulation.ShadowDom
})
export class PieceProductRenderComponent
  extends BaseRenderComponent<PieceProductSettings>
  implements OnInit, OnChanges {

  public product$!: Observable<Product | undefined>;
  public swiperConfig: SwiperOptions = {};
  /**
   * Currently active image details. Used for when hovering replaces an image (like the product gallery or the extra
   * image on desktop)
   */
  public imageDetails: ImageDetails = {};

  /** Image info (color, style, location) for the main imagen */
  private mainImageDetail?: ImageDetails;
  /** Image info extra image (if applicable) */
  private extraImageDetail?: ImageDetails;

  private lastProductId?: string;

  constructor(
    private readonly productsService: ProductsService,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    public readonly renderByDevice: RenderByDeviceService
  ) {
    super();
  }

  @HostBinding('class.disable-interaction')
  public get disableInteraction(): boolean {
    return this.type === 'workspace';
  }

  public get hasToDisplayPrices(): boolean {
    return !!this.piece?.settings?.general?.displayProductPrices;
  }

  public get hasToDisplayProductName(): boolean {
    return !!this.piece?.settings?.general?.displayProductNameToggle;
  }

  public get hasToDisplayProductDescription(): boolean {
    return !!this.piece?.settings?.general?.displayProductDescriptionToggle;
  }

  public get hasToDisplayProductCart(): boolean {
    return !!this.piece?.settings?.general?.displayProductAddToCartToggle;
  }

  public get hasExtraImageEnabled(): boolean {
    return this.piece?.settings?.general?.extraImageToggle === true;
  }

  public get hasToDisplayProductPricesSelector(): boolean {
    return !!this.piece?.settings?.general?.displayProductSizesSelector;
  }

  public ngOnInit() {
    this.swiperConfig = {
      loop: false,
      navigation: false,
      pagination: false,
      slidesPerView: 'auto',
      direction: 'horizontal',
      spaceBetween: 5,
      autoplay: false,
      speed: 1000,
      centeredSlides: false,
      allowTouchMove: true
    };
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.piece && this.piece?.settings?.general?.productId !== this.lastProductId) {
      this.lastProductId = this.piece?.settings?.general?.productId;
      const hasToIncludeStockInformation = !!this.piece?.settings?.general?.displayLastSizeUnitsWarning;
      this.product$ = iif(
        () => !!this.lastProductId,
        this.productsService.productDetailOrNull(this.lastProductId as string, undefined, hasToIncludeStockInformation),
        of(undefined)
      );
    }
    this.initProductParts();
  }

  public getProductImageData(
    product: Product,
    imageDetails?: ImageDetails,
    width = 700
  ): LazyDataInterface | undefined {
    const effectiveImageDetails = imageDetails ?? this.imageDetails;
    const style = effectiveImageDetails?.style != null && Number.isInteger(+effectiveImageDetails.style)
      ? +effectiveImageDetails.style
      : undefined;
    const location = effectiveImageDetails?.location;
    let media: MediaResolved | undefined;
    if (effectiveImageDetails?.colorId) {
      const color = product.colors.find((pColor) => pColor.id === effectiveImageDetails?.colorId);
      if (color) {
        media = ProductsHelpers.firstImageForColor(color, location ?? 'product_page', width, this.getViewStyle(effectiveImageDetails), style);
      } else {
        return undefined;
      }
    } else {
      media = ProductsHelpers.firstImageForFirstColor(product.colors, location ?? 'product_page', width, this.getViewStyle(effectiveImageDetails), style);
    }
    return this.assetsSharedService.productImageData(media, width, this.renderConfigService.config);
  }

  public getBothImages(product: Product) {
    const images = [
      { imageData: this.getProductImageData(product, this.mainImageDetail), imageDetail: this.mainImageDetail },
      { imageData: this.getProductImageData(product, this.extraImageDetail), imageDetail: this.extraImageDetail }
    ];

    return images.every((image) => image.imageData) ? images : undefined;
  }

  public getProductName(productName: string): string {
    if (!!this.piece?.settings?.general.productName) {
      const template = this.piece.settings.general.productName;
      return template.replace(/{{productName}}/g, productName);
    }
    return PRODUCT_NAME_DEFAULT_VALUE.replace(/{{productName}}/g, productName);
  }

  public getProductDescription(productDescription: string): string {
    if (!!this.piece?.settings?.general.productDescription) {
      const template = this.piece.settings.general.productDescription;
      return template.replace(/{{productDescription}}/g, productDescription);
    }
    return PRODUCT_DESCRIPTION_DEFAULT_VALUE.replace(/{{productDescription}}/g, productDescription);
  }

  public getProductLink(product: Product, parts?: ProductPartNumberDetails): Link {
    const link = {
      type: 'product',
      productId: product.id,
      productPartNumberDetails: this.getProductPartsFromDetails(parts)
    };
    return link;
  }

  public onProductClick(product: Product, parts?: ProductPartNumberDetails): void {
    const cmsEvent: CMSEvent<Product> = {
      name: 'productClicked',
      type: 'click',
      data: {
        ...product,
        productPartNumberDetails: this.getProductPartsFromDetails(parts)
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public trackByIdx(idx: number): number {
    return idx;
  }

  /** Hovering over the prouct picture we may want to show the extra image */
  public onMainEnter() {
    if (!this.renderByDevice.isMobile) {
      this.switchToParts('extra');
    }
  }

  public onMainLeave() {
    this.switchToParts('main');
  }

  /** Hovering over a related product we may want (on desktop) to show it on the main picture space */
  public onDetailEnter(product: Product, variant: ProductPartNumberDetails) {
    if (!this.renderByDevice.isMobile) {
      // We inherite location and viewStyle from the main image settings, not from the extra image
      this.switchToParts('main');
      this.imageDetails.colorId = variant.colorId;
      this.imageDetails.style = variant.style;
    }
  }

  public onDetailLeave() {
    this.switchToParts('main');
  }

  private initProductParts() {
    this.mainImageDetail = {
      colorId: this.piece?.settings?.general?.mainColorId,
      style: this.piece?.settings?.general?.mainStyle,
      location: this.piece?.settings?.general?.mainLocation,
      viewStyle: this.piece?.settings?.general?.mainViewStyle,
      viewStyleMediaId: this.piece?.settings?.general?.mainMediaId
    };

    this.extraImageDetail = {
      colorId: this.piece?.settings?.general?.extraColorId,
      style: this.piece?.settings?.general?.extraStyle,
      location: this.piece?.settings?.general?.extraLocation,
      viewStyle: this.piece?.settings?.general?.extraViewStyle,
      viewStyleMediaId: this.piece?.settings?.general?.extraMediaId
    };
    this.switchToParts('main');
  }

  private switchToParts(parts: 'main' | 'extra') {
    this.imageDetails = { ...(parts === 'main' || !this.hasExtraImageEnabled ? this.mainImageDetail : this.extraImageDetail) };
  }

  private getProductPartsFromDetails(details?: ImageDetails | ProductPartNumberDetails): ProductPartNumberDetails {
    const effectiveDetails = details ?? this.imageDetails;

    return {
      colorId: effectiveDetails.colorId,
      style: effectiveDetails.style
    };
  }

  private getViewStyle(imageDetails?: ImageDetails): string | undefined {
    switch (imageDetails?.viewStyle) {
      case 'silhouette':
        return '_2_4';
      case 'lookbook':
        return '_2_10';
      case 'main':
        return 'main';
      case 'media-id':
      case 'extra-media-id':
        return imageDetails?.viewStyleMediaId;
      default:
        return undefined; // In this case return main medias
    }
  }

}
